/**
 * Stopping an impersonation session safely (#6448).
 *
 * While an officer impersonates a customer, the running session holds the customer's
 * entries, schedule and plan in memory, and writes go to a temporary local copy of the
 * customer's database. The old "Stop Impersonating" pointed writes back at the officer's own
 * database INSIDE that session and then opened the home page. The home page's weight
 * estimate rebuilt the backlog from the customer's entries still in memory and saved every
 * finished past entry into the officer's database, which synced them to the server. On
 * 2026-10-02 that put 27 of a customer's entries into an officer's account.
 *
 * The rule: never change the write target inside a running session. Stop clears the flag,
 * marks the temporary copy for deletion and reloads; the NEXT start deletes the copy before
 * any database opens, so nothing holding the customer's data is alive when it goes.
 * See design-docs/admin-user-db-download.md, "Stopping impersonation".
 */

/** localStorage key: the name of a temporary impersonation copy the next start must delete */
export const IMPERSONATION_COPY_PENDING_DELETE_KEY = 'impersonationCopyPendingDelete';

export interface StopImpersonationSteps {
  /** forget which account is being impersonated (the localStorage flag) */
  clearImpersonationFlag(): void;
  /** record the temporary copy's name, so the next start deletes it */
  markTemporaryCopyForDeletion(): void;
  /** restart the app from scratch; nothing of this session survives it */
  reloadAppAsync(): Promise<void>;
}

/** Nothing here may point writes elsewhere or open a page: the reload is the only way out. */
export async function stopImpersonatingAsync(steps: StopImpersonationSteps): Promise<void> {
  steps.clearImpersonationFlag();
  steps.markTemporaryCopyForDeletion();
  await steps.reloadAppAsync();
}

export enum TemporaryDbChange {
  /** nothing changes (same database, or none before and none after) */
  Unchanged = 'unchanged',
  /** a fresh wrapper is routed to a temporary database for the first time */
  FirstRouting = 'first-routing',
  /** an already-routed wrapper would start writing somewhere else: refused */
  Refused = 'refused',
}

/**
 * Whether a wrapper may change its temporary write target. It may be set once, at start-up,
 * before the documents load. After that it never changes in place: the session holds the
 * documents it read from that target, and anything that saves them would write them into
 * the new one. Leaving a temporary target goes through a reload instead.
 */
export function decideTemporaryDbChange(current: unknown, next: unknown): TemporaryDbChange {
  if (current === next || (current == null && next == null)) return TemporaryDbChange.Unchanged;
  if (current == null) return TemporaryDbChange.FirstRouting;
  return TemporaryDbChange.Refused;
}

export enum PendingCopyAction {
  /** no copy is waiting */
  Nothing = 'nothing',
  /** the copy is the one this start is about to use again: keep it, drop the marker */
  KeepAndForget = 'keep-and-forget',
  /** delete the copy, then drop the marker */
  Delete = 'delete',
}

/**
 * What a start does with a copy a stopped impersonation left behind. The officer may already
 * have started impersonating the same customer again before this start ran; that copy is in
 * use and must survive.
 */
export function decidePendingCopyAction(pendingName: string | null, namesThisStartUses: readonly string[]): PendingCopyAction {
  if (!pendingName) return PendingCopyAction.Nothing;
  if (namesThisStartUses.includes(pendingName)) return PendingCopyAction.KeepAndForget;
  return PendingCopyAction.Delete;
}

export interface PendingCopyDeleteSteps {
  /** the name a stopped impersonation recorded, or null; throws when localStorage cannot be read */
  readMarker(): string | null;
  removeMarker(): void;
  /** every local database this start is about to open, the signed-in user's own one included */
  namesThisStartUses: readonly string[];
  destroyDatabaseAsync(name: string): Promise<void>;
  reportProblem(name: string, err: unknown): void;
}

/**
 * Run at the top of every start, before any database opens. Never throws: a start must not fail
 * over a leftover copy, which is never synced anywhere and so is clutter, not a leak. A failed
 * delete keeps the marker, so the next start tries again.
 */
export async function deletePendingCopyAsync(steps: PendingCopyDeleteSteps): Promise<void> {
  let pendingName: string | null;
  try {
    pendingName = steps.readMarker();
  } catch (err) {
    steps.reportProblem('ImpersonationCopyMarkReadFailed', err);
    return;
  }
  const action = decidePendingCopyAction(pendingName, steps.namesThisStartUses);
  if (action === PendingCopyAction.Nothing) return;
  try {
    if (action === PendingCopyAction.Delete) {
      await steps.destroyDatabaseAsync(pendingName);
    }
    steps.removeMarker();
  } catch (err) {
    steps.reportProblem('ImpersonationCopyDeleteFailed', err);
  }
}

/**
 * Whether the rollback of a FAILED impersonation download deletes the partial copy. It never
 * touches the copy the session is writing to: an officer already impersonating someone can start
 * a second impersonation, and if that is the same person the download landed in the very
 * database in use (same name, same store).
 */
export function shouldDiscardFailedDownload(partialName: string | null | undefined, inUseName: string | null | undefined): boolean {
  if (!partialName) return false;
  return partialName !== inUseName;
}
